import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import { CreationDetailFragment } from './../../generated/graphql';

const modificationRequestDeliverableForDetailFragment = gql`
  fragment ModificationRequestDeliverableForDetail on ModificationRequestDeliverable {
    id
    createdAt
    creative {
      id
      file {
        id
        originalFileName
        url
        mimeType
        size
      }
    }
    attachedThumbnail {
      id
      file {
        id
        originalFileName
        url
        mimeType
        size
      }
    }
    intent
  }
`;

export const modificationRequestItemIssueCommentForDetailFragment = gql`
  fragment ModificationRequestItemIssueCommentForDetail on IssueComment {
    id
    body
    assetIds
    createdAt
    user {
      ... on UserDirector {
        id
        name
        email
        profileImageUrl
      }
      ... on UserOptimizer {
        id
        name
        email
        profileImageUrl
      }
    }
  }
`;

export const modificationRequestItemIssueForDetailFragment = gql`
  ${modificationRequestItemIssueCommentForDetailFragment}
  fragment ModificationRequestItemIssueForDetail on Issue {
    id
    name
    resolved
    createdAt
    comments {
      ...ModificationRequestItemIssueCommentForDetail
    }
  }
`;

export const modificationRequestItemForDetailFragment = gql`
  ${modificationRequestItemIssueForDetailFragment}
  fragment ModificationRequestItemForDetail on RequestItem {
    id
    bucketId
    type
    typeName
    assetIds
    body
    metadata
    sender {
      id
      name
      profileImageUrl
    }
    issue {
      ...ModificationRequestItemIssueForDetail
    }
  }
`;

export const modificationRequestForDetailFragment = gql`
  ${modificationRequestDeliverableForDetailFragment}
  ${modificationRequestItemForDetailFragment}
  fragment ModificationRequestForDetail on Request {
    id
    bucketId
    state
    targetDeliverableId
    feeType
    createdAt
    submittedAt
    approvedAt
    deliveryDue
    feedback {
      id
      body
      createdAt
    }
    sender {
      id
      name
      profileImageUrl
    }
    items {
      ...ModificationRequestItemForDetail
    }
    deliverables {
      ...ModificationRequestDeliverableForDetail
    }
  }
`;

export const creativeForCreationDetailFragment = gql`
  fragment CreativeForCreationDetail on Creative {
    id
    file {
      id
      originalFileName
      url
      mimeType
      size
    }
  }
`;

const deliverableForCraetionDetailFragment = gql`
  ${creativeForCreationDetailFragment}
  fragment DeliverableForCraetionDetail on Deliverable {
    id
    creative {
      ...CreativeForCreationDetail
    }
    attachedThumbnail {
      id
      file {
        id
        originalFileName
        url
        mimeType
        size
      }
    }
    intent
    state
    uploadedAdPlatformKeys
    createdAt
    stateUpdatedAt
  }
`;

export const creationDetailFragment = gql`
  ${modificationRequestForDetailFragment}
  ${deliverableForCraetionDetailFragment}
  fragment CreationDetail on Creation {
    id
    assignmentId
    projectId
    creationSetId
    latestDeliverableUrl
    assignee {
      id
      name
      profileImageUrl
    }
    finalized
    finalizedAt
    deliverables {
      ...DeliverableForCraetionDetail
    }
    modificationRequests {
      ...ModificationRequestForDetail
    }
    state
    proposed
    submittedAt
    createdAt
    stateUpdatedAt
    modificationRequestExpired
    modificationRequestFreeExpiredAt
    modificationRequestPoint
    modificationRequestFreeLimit
    isNextModificationRequestForAFee
    creativeFormat {
      id
      network {
        id
        key
        name
      }
      placement {
        id
        key
        name
        basicMerchandises {
          isModificationRequestable
          creativeType {
            id
            key
            name
          }
        }
      }
      attribute {
        aspectRatio {
          supportingAspectRatioId
          size {
            height
            width
          }
          aspectRatio {
            height
            width
          }
          requiredOption {
            amount
            points {
              key
              point
            }
          }
        }
        playbackTime {
          supportingPlaybackTimeId
          seconds
          requiredOption {
            amount
            points {
              key
              point
            }
          }
        }
      }
      creativeType {
        id
        key
        name
      }
    }
    organization {
      id
      name
      pointBalance
      features {
        canOpenOfferWhenDisagreed
      }
      businessRegion {
        key
        idBusinessRegion
        timeZone
      }
    }
  }
`;

class CreationWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  public getByIdWithoutPricingData = async (creationId: string): Promise<CreationDetailFragment> => {
    const result = await this.graphqlClient.query(
      gql`
        ${creationDetailFragment}
        query getCreationById($creationId: ID!) {
          creation(id: $creationId) {
            ...CreationDetail
          }
        }
      `,
      { creationId },
    );

    return result.creation as CreationDetailFragment;
  };
}

export default CreationWebApi;
